/* eslint-disable @typescript-eslint/no-empty-interface */
import React from "react"
import type {} from "@mui/x-date-pickers/themeAugmentation"
import { createTheme, PaletteColor, PaletteColorOptions, ThemeProvider } from "@mui/material"
import { I18nextProvider } from "react-i18next"
import { BrowserRouter } from "react-router-dom"
import { HTTPLogger } from "src/utils/logger/HTTPLogger"
import ErrorOutlineOutlinedIcon from "@mui/icons-material/ErrorOutlineOutlined"
import WarningAmberOutlined from "@mui/icons-material/WarningAmberOutlined"

import i18n from "../i18n"
import { LoggerProvider } from "./LoggerProvider"
import { UserProvider } from "./UserContext"
import { GlobalLoaderProvider } from "./GlobalLoaderProvider"
import { SnackbarProvider } from "./SnackbarProvider"
import { UsageLimitProvider } from "./UsageLimitProvider"
import { UserLanguagesProvider } from "./UserLanguagesContext"

declare module "@mui/material/styles" {
  interface PaletteColorWithShades extends PaletteColor {
    50?: string
    100?: string
    200?: string
    300?: string
    400?: string
    500?: string
    600?: string
    700?: string
    800?: string
    900?: string
    A100?: string
    A200?: string
    A400?: string
    A700?: string
  }
  interface CustomPaletteOptions {
    lightGold: PaletteColorOptions
    midnightBlue: PaletteColorOptions
    lightSkyBlue: PaletteColorOptions
    electricMint: PaletteColorOptions
    gray: PaletteColorOptions
    pastelRed: PaletteColorOptions
  }
  interface CustomPalette {
    lightGold: PaletteColorWithShades
    midnightBlue: PaletteColorWithShades
    lightSkyBlue: PaletteColorWithShades
    electricMint: PaletteColorWithShades
    gray: PaletteColorWithShades
    pastelRed: PaletteColorWithShades
  }
  interface Palette extends CustomPalette {}
  interface PaletteOptions extends CustomPaletteOptions {}
}

declare module "@mui/material/Chip" {
  interface ChipPropsColorOverrides {
    lightGold: true
    midnightBlue: true
    electricMint: true
    lightSkyBlue: true
    gray: true
    pastelRed: true
  }
}

declare module "@mui/material/IconButton" {
  interface IconButtonPropsColorOverrides {
    lightGold: true
    midnightBlue: true
    electricMint: true
    gray: true
    pastelRed: true
  }
}

declare module "@mui/material/Button" {
  interface ButtonPropsColorOverrides {
    lightGold: true
    midnightBlue: true
    electricMint: true
    gray: true
  }

  interface ButtonPropsVariantOverrides {
    underlined: true
  }
}

const primary50 = "#E9FAFF" // vs #D5F5FF 
export const primary75 = "#E6FBFF"
const primary100 = "#A4E9FF"
export const primary150 = "#AFEFFF"
const primary200 = "#6BE3FD"
const primaryMain = "#00D2FF" //300

const gray100 = "#E8E8E8"
const grayMain = "#9E9E9E"

const pastelRed100 = "#FB9696"
const pastelRed = "#E68585" //500

const lightGold50 = "#FFFBF1"
const lightGold100 = "#FBE8B3" //
const lightGold = "#F6D887" //200

const electricMint50 = "#F0FFFE"
const electricMint100 = "#BEFFFB"
const electricMint = "#27E6DC" //200

const midnightBlue = "#01294E"

const lightSkyBlue = "#C8F4FF"

const disabledBackground = "#F4F5F7"
const gray = "#CCCCCC"

const primaryColorForText = midnightBlue

export const scrollbarMixin = (size: "small" | "medium" | "large" = "large", backgroundColor = "#F2F2F2") => ({
  "&::-webkit-scrollbar": {
    width: size == "large" ? "12px" : size == "medium" ? "8px" : "5px",
    backgroundColor: backgroundColor,
    borderRadius: "50px"
  },
  "&::-webkit-scrollbar-thumb": {
    border: size == "large" ? "2px solid transparent" : "1px solid transparent",
    backgroundClip: "content-box",
    backgroundColor: gray,
    borderRadius: "6px"
  },
  "&::-webkit-scrollbar-track": {
    background: "transparent"
  }
})

export const appBarHeightPixels = 80
export const appBarHeight = `${appBarHeightPixels}px`

const { palette, spacing } = createTheme()

const theme = createTheme({
  typography: {
    fontFamily: "'Inter', sans-serif",
    allVariants: {
      color: primaryColorForText
    },
    h3: {
      fontSize: "1.75rem",
      lineHeight: "2.25rem",
      fontWeight: 500
    },
    h4: {
      fontSize: "1.5rem",
      fontWeight: 500
    },
    h5: {
      fontSize: "1.25rem",
      lineHeight: "28px",
      fontWeight: 500
    },
    h6: {
      fontSize: "1rem",
      lineHeight: "21px",
      fontWeight: 500
    },
    body2: {
      fontSize: "0.8rem",
      color: "#8C97A8"
    }
  },
  components: {
    MuiButton: {
      variants: [
        {
          props: { variant: "underlined" },
          style: {
            textDecoration: "underline",
            textDecorationStyle: "dashed",
            "&:hover": {
              textDecoration: "underline",
              textDecorationStyle: "solid",
              background: "transparent",
              boxShadow: "none"
            }
          }
        }
      ],
      styleOverrides: {
        root: {
          borderRadius: "30px",
          textTransform: "none",
          color: primaryColorForText,
          boxShadow: "none",
          "&:hover": {
            boxShadow: "0px 0px 15px #01294E26" //midnightblue with alpha
          },
          "&.MuiButton-containedWarning": {
            "&:hover": {
              backgroundColor: "#EE9C3C"
            }
          },
          "&.MuiButton-containedInfo": {
            backgroundColor: primaryMain,
            "&:hover": {
              backgroundColor: primaryMain
            }
          },
          "&.MuiButton-containedLightGold": {
            background: lightGold100,
            "&:hover": {
              background: lightGold
            }
          },
          "&.MuiButton-containedElectricMint": {
            background: electricMint100,
            "&:hover": {
              background: electricMint
            }
          },
          "&.MuiButton-sizeSmall": {
            padding: "4px 16px"
          }
        },
        outlined: {
          "&.Mui-disabled": {
            backgroundColor: disabledBackground
          }
        },
        outlinedPrimary: {
          border: "1px solid",
          borderColor: primaryMain,
          "&:hover": {
            borderColor: primary200
          }
        },
        containedPrimary: {
          "&:hover": {
            background: primaryMain
          }
        },
        containedSecondary: {
          background: primary100,
          "&:hover": {
            background: primaryMain
          }
        },
        outlinedSecondary: {
          borderColor: "#CCCCCC",
          backgroundColor: palette.common.white,
          "&:hover": {
            borderColor: "#898989"
          }
        },
        textPrimary: {
          "&:hover": {
            background: "transparent",
            boxShadow: "none"
          }
        }
      }
    },
    MuiIconButton: {
      styleOverrides: {
        root: {
          color: "#898989",
          "&:hover": {
            background: primary150,
            color: primaryColorForText
          },
          "&.MuiIconButton-colorLightGold": {
            backgroundColor: lightGold100,
            color: primaryColorForText,
            "&:hover": {
              boxShadow: `2px 2px 10px ${lightGold100}`
            }
          },
          "&.MuiIconButton-colorGray": {
            backgroundColor: gray100,
            color: primaryColorForText,
            "&:hover": {
              boxShadow: `2px 2px 10px ${gray100}`
            }
          },
          "&.MuiIconButton-colorPastelRed": {
            backgroundColor: pastelRed100,
            color: primaryColorForText,
            "&:hover": {
              boxShadow: `2px 2px 10px ${pastelRed100}`
            }
          },
          "&.MuiIconButton-colorPrimary": {
            backgroundColor: primary100,
            color: primaryColorForText,
            "&:hover": {
              boxShadow: `2px 2px 10px ${primary100}`
            }
          },
          "&.MuiIconButton-colorElectricMint": {
            backgroundColor: electricMint100,
            color: primaryColorForText,
            "&:hover": {
              boxShadow: `2px 2px 10px ${electricMint100}`
            }
          },
          "&.MuiIconButton-colorMidnightBlue": {
            backgroundColor: primary100,
            color: primaryColorForText,
            "&:hover": {
              boxShadow: `2px 2px 10px ${primary100}`
            }
          }
        }
      }
    },
    MuiTooltip: {
      styleOverrides: {
        tooltip: {
          background: primaryColorForText,
          borderRadius: "30px",
          padding: "15px 20px"
        }
      }
    },
    MuiOutlinedInput: {
      styleOverrides: {
        root: {
          backgroundColor: palette.common.white,
          borderColor: "#0000001F",
          "&:hover .MuiOutlinedInput-notchedOutline": {
            boxShadow: "2px 2px 10px #0000001A",
            border: "2px solid",
            borderColor: gray
          },
          "&.Mui-focused .MuiOutlinedInput-notchedOutline": {
            border: "2px solid",
            borderColor: gray
          }
        },
        input: {
          color: primaryColorForText
        }
      }
    },
    MuiInputLabel: {
      styleOverrides: {
        shrink: {
          color: "#01294E",
          fontWeight: 600,
          "&.Mui-focused": {
            color: "#01294E"
          }
        }
      }
    },
    MuiAlert: {
      styleOverrides: {
        //filled is used by snackbar
        filled: {
          boxShadow: "0px 0px 15px #01294E40", //midnightblue with alpha
          borderRadius: "15px",
          color: primaryColorForText
        },
        filledInfo: {
          background: `${primary75} 0% 0% no-repeat padding-box`
        },
        filledError: {
          backgroundColor: "#FFB3B3"
        },
        standard: {
          padding: spacing(0, 2)
        },
        standardError: {
          color: "#EC5351",
          backgroundColor: "#FFF8F8",
          "&.MuiAlert-standard": {
            border: "1px solid #F8D0D0"
          },
          "& .MuiAlert-icon": {
            color: "#EC5351"
          }
        },
        standardWarning: {
          color: "#EE9C3C",
          backgroundColor: "#FFF7EE",
          "&.MuiAlert-standard": {
            border: "1px solid #FFDAAE"
          },
          "& .MuiAlert-icon": {
            color: "#EE9C3C"
          }
        },
        standardInfo: {
          color: "#555657",
          backgroundColor: "#FFFFFF",
          "&.MuiAlert-standard": {
            border: "1px solid #E9ECF0"
          }
        }
      },
      defaultProps: {
        iconMapping: {
          error: <ErrorOutlineOutlinedIcon />,
          warning: <WarningAmberOutlined />,
          info: <></>,
          success: <></>
        }
      }
    },
    MuiAutocomplete: {
      styleOverrides: {
        listbox: {
          maxHeight: "190px",
          ...scrollbarMixin("small")
        },
        option: {
          whiteSpace: "nowrap",
          overflow: "hidden",
          textOverflow: "ellipsis"
        }
      }
    },
    MuiDialog: {
      styleOverrides: {
        paper: {
          borderRadius: "25px"
        }
      }
    },
    MuiDialogActions: {
      styleOverrides: {
        root: {
          justifyContent: "flex-start"
        }
      }
    },
    MuiDialogContent: {
      styleOverrides: {
        root: {
          ...scrollbarMixin()
        }
      }
    },
    MuiBackdrop: {
      styleOverrides: {
        root: {
          backgroundColor: "#01294E80" //midnightblue with alpha
        }
      }
    },
    MuiDatePicker: {
      defaultProps: {
        slotProps: {
          day: {
            disableRipple: true,
            sx: {
              "&.MuiPickersDay-today": {
                borderColor: "#C4F2FF"
              },
              "&.Mui-selected": {
                backgroundColor: "#D5F5FF",
                "&:focus": {
                  backgroundColor: "#D5F5FF"
                }
              },
              "&:hover": {
                backgroundColor: "#00D2FF"
              }
            }
          },
          openPickerButton: {
            size: "small",
            sx: {
              "& svg": {
                fontSize: "20px"
              }
            }
          }
        }
      }
    }
  },
  palette: {
    text: {
      primary: midnightBlue
    },
    primary: {
      light: primary50,
      main: primaryMain,
      100: primary100,
      200: primary200,
      400: "#00C5FF",
      contrastText: midnightBlue
    },
    warning: {
      main: "#EE9C3C",
      // light: '#FFF7EE', // would override alert icon color
      contrastText: palette.common.white
    },
    info: {
      main: "#555657"
    },
    success: {
      main: "#019592"
    },
    action: {
      disabledBackground: disabledBackground,
      disabled: gray
    },
    lightGold: {
      main: lightGold,
      light: lightGold50,
      dark: "#E6C25A", // darker variant
      50: lightGold50,
      100: lightGold100, // lighter variant
      200: lightGold,
      300: "#F8DC95", // lighter
      400: "#FAD483", // even lighter
      500: lightGold,
      600: "#E6C25A", // slightly darker
      700: "#D4B04A", // darker
      800: "#C29E3A", // even darker
      900: "#B08C2A", // darkest
      A100: "#FFFEF7", // very light
      A200: "#FFF9D1", // light
      A400: "#FFF4AB", // medium light
      A700: "#FFF185", // medium
      contrastText: midnightBlue
    },
    gray: {
      main: grayMain,
      light: "#BDBDBD",
      dark: "#757575",
      50: "#FAFAFA",
      100: gray100,
      200: "#EEEEEE",
      300: "#E0E0E0",
      400: "#BDBDBD",
      500: grayMain,
      600: "#757575",
      700: "#616161",
      800: "#424242",
      900: "#212121",
      A100: "#F5F5F5",
      A200: "#EEEEEE",
      A400: "#BDBDBD",
      A700: "#616161",
      contrastText: "#FFFFFF"
    },
    pastelRed: {
      main: pastelRed, // 500 variant (darker than 100)
      light: "#FFB3B3", // lighter variant
      dark: "#D47373", // darker variant
      50: "#FFF8F8", // very light
      100: pastelRed100, // "#FB9696" - this is the 100 variant
      200: "#F8A3A3", // slightly darker than 100
      300: "#F5B0B0", // even darker
      400: "#F2BDBD", // darker still
      500: pastelRed, // main variant (darker than 100)
      600: "#E07878", // slightly darker
      700: "#D47373", // darker
      800: "#C26161", // even darker
      900: "#B04F4F", // darkest
      A100: "#FFF5F5", // very light
      A200: "#FFE0E0", // light
      A400: "#FFCBCB", // medium light
      A700: "#FFB6B6", // medium
      contrastText: midnightBlue
    },
    midnightBlue: {
      main: midnightBlue,
      light: primary50,
      dark: "#011F3F", // darker variant
      50: "#F0F8FF", // very light
      100: "#E1F1FF", // light
      200: "#C3E3FF", // lighter
      300: "#A5D5FF", // even lighter
      400: "#87C7FF", // medium light
      500: midnightBlue,
      600: "#011F3F", // slightly darker
      700: "#011A35", // darker
      800: "#01152B", // even darker
      900: "#011021", // darkest
      A100: "#E6F3FF", // very light
      A200: "#CCE7FF", // light
      A400: "#B3DBFF", // medium light
      A700: "#99CFFF", // medium
      contrastText: primary75 // label in header in FileCard
    },
    lightSkyBlue: {
      main: lightSkyBlue,
      light: "#D8F7FF", // lighter variant
      dark: "#A8E4F0", // darker variant
      50: "#F7FEFF", // very light
      100: "#EFFDFF", // light
      200: lightSkyBlue,
      300: "#C8F4FF", // lighter
      400: "#B8F0FF", // even lighter
      500: lightSkyBlue,
      600: "#A8E4F0", // slightly darker
      700: "#98D8E0", // darker
      800: "#88CCD0", // even darker
      900: "#78C0C0", // darkest
      A100: "#F0FDFF", // very light
      A200: "#E1FBFF", // light
      A400: "#D2F9FF", // medium light
      A700: "#C3F7FF", // medium
      contrastText: midnightBlue
    },
    electricMint: {
      main: electricMint,
      light: electricMint50,
      dark: "#1FB8B0", // darker variant
      50: electricMint50,
      100: electricMint100,
      200: electricMint,
      300: "#4DE9E0", // lighter variant
      400: "#73EDE5", // even lighter
      500: electricMint,
      600: "#22D3C9", // slightly darker
      700: "#1DB8AE", // darker
      800: "#189A92", // even darker
      900: "#137D77", // darkest
      A100: "#E6FFFE", // very light
      A200: "#B3FFFC", // light
      A400: "#80FFF9", // medium light
      A700: "#66FFF7", // medium
      contrastText: midnightBlue
    }
  }
})

interface Props {
  children: React.ReactNode
}
const AppProviders = ({ children }: Props) => {
  return (
    <UserProvider>
      <NoUserContextAppProviders>
        <UsageLimitProvider>
          <UserLanguagesProvider>{children}</UserLanguagesProvider>
        </UsageLimitProvider>
      </NoUserContextAppProviders>
    </UserProvider>
  )
}
export default AppProviders

export const NoUserContextAppProviders = ({ children }: Props) => {
  const logger = new HTTPLogger()

  return (
    <ThemeProvider theme={theme}>
      <LoggerProvider logger={logger}>
        <SnackbarProvider>
          <BrowserRouter>
            <I18nextProvider i18n={i18n}>
              <GlobalLoaderProvider>{children}</GlobalLoaderProvider>
            </I18nextProvider>
          </BrowserRouter>
        </SnackbarProvider>
      </LoggerProvider>
    </ThemeProvider>
  )
}
